import React from 'react';
import styled from 'styled-components';
import { useAuth } from '../../contexts/AuthContext';
import { logout } from '../../firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Button } from '../ui/Button';
import { theme } from '../../theme';
import { FiMenu, FiBell, FiLogOut, FiUser } from 'react-icons/fi';

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[6]};
  height: 70px;
`;

const LeftSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  flex: 1;
  min-width: 0;
`;

const PageTitle = styled.h1`
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.light.text.primary};
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize.lg};
  }
  
  @media (max-width: ${theme.breakpoints.sm}) {
    display: none;
  }
`;

const MenuButton = styled.button`
  display: none;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.medium};
  background-color: ${theme.colors.white};
  color: ${theme.colors.dark.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    border-color: ${theme.colors.light.border.dark};
  }

  @media (max-width: ${theme.breakpoints.lg}) {
    display: flex;
  }
`;


const RightSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
`;

const IconButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.md};
  border: 1px solid ${theme.colors.light.border.medium};
  background-color: ${theme.colors.white};
  color: ${theme.colors.light.text.primary};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
    border-color: ${theme.colors.light.border.dark};
  }
`;

const NotificationBadge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 18px;
  height: 18px;
  border-radius: ${theme.borderRadius.full};
  background-color: ${theme.colors.accent.danger};
  color: ${theme.colors.white};
  font-size: 10px;
  font-weight: ${theme.typography.fontWeight.bold};
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UserSection = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding-left: ${theme.spacing[3]};
  border-left: 1px solid ${theme.colors.light.border.medium};
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;

  @media (max-width: ${theme.breakpoints.sm}) {
    display: none;
  }
`;

const UserName = styled.span`
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.light.text.primary};
`;

const UserRole = styled.span`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.light.text.secondary};
  text-transform: capitalize;
`;

const UserAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: linear-gradient(135deg, ${theme.colors.primary[500]}, ${theme.colors.primary[700]});
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.white};
  font-weight: ${theme.typography.fontWeight.bold};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  transition: transform 0.2s ease;

  &:hover {
    transform: scale(1.05);
  }
`;

const DropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + ${theme.spacing[2]});
  right: 0;
  background-color: ${theme.colors.white};
  border: 1px solid ${theme.colors.light.border.medium};
  border-radius: ${theme.borderRadius.md};
  box-shadow: ${theme.shadows.lg};
  min-width: 200px;
  overflow: hidden;
  z-index: 1000;
`;

const DropdownItem = styled.button`
  width: 100%;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  text-align: left;
  border: none;
  background: none;
  color: ${theme.colors.light.text.primary};
  font-size: ${theme.typography.fontSize.sm};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  transition: background-color 0.2s ease;

  &:hover {
    background-color: ${theme.colors.light.background.tertiary};
  }

  svg {
    font-size: ${theme.typography.fontSize.base};
  }
`;

interface DashboardHeaderProps {
  onMenuClick: () => void;
}

const DashboardHeader: React.FC<DashboardHeaderProps> = ({ onMenuClick }) => {
  const { user, userData, companyData, staffData } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showDropdown, setShowDropdown] = React.useState(false);
  const dropdownRef = React.useRef<HTMLDivElement>(null);

  // Get page title based on route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path.includes('/attendance')) return 'Guard Attendance';
    if (path.includes('/guards')) return 'Guards';
    if (path.includes('/deployment')) return 'Deployment';
    if (path.includes('/clients')) return 'Clients';
    if (path.includes('/staff')) return 'Staff';
    if (path.includes('/dashboard')) return 'Dashboard';
    if (path.includes('/reports')) return 'Reports';
    if (path.includes('/settings')) return 'Settings';
    return 'Dashboard';
  };

  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Failed to logout');
    }
  };

  const getInitials = (email: string | undefined, displayName: string | undefined) => {
    if (displayName) {
      return displayName
        .split(' ')
        .map(n => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    if (email) {
      return email[0].toUpperCase();
    }
    return 'U';
  };

  return (
    <HeaderContainer>
      <LeftSection>
        <MenuButton onClick={onMenuClick}>
          <FiMenu size={20} />
        </MenuButton>
        <PageTitle>{getPageTitle()}</PageTitle>
      </LeftSection>

      <RightSection>
        <IconButton title="Notifications">
          <FiBell size={20} />
          <NotificationBadge>3</NotificationBadge>
        </IconButton>

        <UserSection>
          <UserInfo>
            <UserName>{user?.email || userData?.email || 'User'}</UserName>
            <UserRole>
              {staffData
                ? (staffData.department || staffData.departments?.[0] || staffData.role || 'staff')
                : (companyData ? 'admin' : (userData?.role || 'user'))}
            </UserRole>
          </UserInfo>
          <div style={{ position: 'relative' }} ref={dropdownRef}>
            <UserAvatar onClick={() => setShowDropdown(!showDropdown)}>
              {getInitials(user?.email || userData?.email, userData?.displayName || user?.displayName || undefined)}
            </UserAvatar>
            {showDropdown && (
              <DropdownMenu>
                <DropdownItem>
                  <FiUser />
                  Profile
                </DropdownItem>
                <DropdownItem onClick={handleLogout}>
                  <FiLogOut />
                  Logout
                </DropdownItem>
              </DropdownMenu>
            )}
          </div>
        </UserSection>
      </RightSection>
    </HeaderContainer>
  );
};

export default DashboardHeader;

